import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import xss from "xss";
import classNames from "classnames";
import { CircleAlert, ReceiptText, Send, Smartphone, Tag } from "lucide-react";
import { backendLink, backendLinkComplaint } from "../config";
import MetaTags from "../components/MetaTagsReclamation";
import { usePwaInstall } from "../components/hooks/usePwaInstall";
import { sendSubscriptionToBackend } from "../components/hooks/useNotifications";
import {
  styles,
  Drawer,
  EMPTY_LOT,
  ErrorBox,
  Field,
  LotFields,
  MessageBox,
  MessageLabel,
  NextButton,
  PageShell,
  ProductPicker,
  Question,
  Reveal,
  Select,
  ThanksScreen,
  TypeGrid,
  asFile,
  firstMissing,
  isEmpty,
  localized,
  lotSummary,
  parseDate,
  toggleType,
  useDrawers,
  useInstallApp,
  flagField,
} from "../components/wizard";

// Obligatory fields checked when leaving each step; the others are checked on submit
const STEP_FIELDS = {
  problem: ["type_complaints"],
  product: ["products"],
  lot: ["n_lot", "fabrication_date"],
};

// PLACEHOLDER: the server does not serve the "since when" list yet (merchant/fromWhen). Until it
// does, the prototype's five answers are shown. They have no server id, so no from_when_id is sent
// for them; the list from the server replaces them as soon as it returns values.
const DEFAULT_DELAYS = [1, 2, 3, 4, 5].map((n) => ({ id: `default-${n}`, labelKey: `rmSince${n}`, isDefault: true }));

// Obligatory fields answered in the last section (prototype "Racontez-nous le problème")
const MESSAGE_FIELDS = ["comment", "img_r", "voice_r"];

// One rule per obligatory field sent by the tenant config; true = filled.
// Lot and date are not asked when the complaint is about no product or about all of them.
const FIELD_RULES = {
  all_products: () => true,
  merchant_id: () => true,
  products: (d) => d.has_no_product || d.tous || !!d.product,
  n_lot: (d) => d.has_no_product || d.tous || !isEmpty(d.n_lot) || d.compressedLot.length > 0,
  fabrication_date: (d) => d.has_no_product || d.tous || !(isEmpty(d.date) && d.compressedLot.length === 0),
  from_where_id: (d) => !isEmpty(d.source),
  from_when_id: (d) => d.has_no_product || !isEmpty(d.delay),
  type_complaints: (d) => d.selectedTypes.length > 0,
  img_r: (d) => d.compressedFiles.length > 0,
  voice_r: () => true,
  channel_id: () => true,
};

// Characters the previous form removed from the lot number
const LOT_FILTER = /[^a-zA-Z0-9 \-_/,:]/g;

// ── Merchant code ──────────────────────────────────────────────────────────────

// "Create a merchant account" icon of the prototype (a shop with a plus)
const StorePlus = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
    <path d="M4 12v8a2 2 0 0 0 2 2h7" />
    <path d="M2 7h20v3a2 2 0 0 1-2 2 2.7 2.7 0 0 1-2-1 2.7 2.7 0 0 1-4 0 2.7 2.7 0 0 1-4 0 2.7 2.7 0 0 1-4 0 2.7 2.7 0 0 1-2 1 2 2 0 0 1-2-2V7" />
    <path d="M19 15v6" />
    <path d="M16 18h6" />
  </svg>
);

// Shown when the merchant has no session yet (prototype "mcode" screen): 6-character code,
// "create my merchant account", install link. The code is checked as soon as the 6th character
// is typed: there is no button to log in. The field is read-only while the code is checked.
function MerchantCode({ currentLang, setCurrentLang, onAuthenticated }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canInstall, install, modals } = useInstallApp();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [attn, setAttn] = useState(0); // the "create" button flashes after an unknown code
  const codeRef = useRef(null);
  const createRef = useRef(null);

  // as in the prototype, the code field is ready to type in
  useEffect(() => {
    const timer = setTimeout(() => codeRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const btn = createRef.current;
    if (!attn || !btn) return;
    btn.classList.remove(styles.attn);
    void btn.offsetWidth;
    btn.classList.add(styles.attn);
  }, [attn]);

  const submitCode = (value) => {
    if (value.length < 6 || checking) return;
    const formData = new FormData();
    formData.append("hashed_code", value);
    setChecking(true);
    axios
      .post(`${backendLink}merchant/loginMerchant`, formData, { withCredentials: true })
      .then(() => {
        if ("serviceWorker" in navigator && "PushManager" in window) {
          navigator.serviceWorker.ready.then((registration) => {
            registration.pushManager.getSubscription().then((subscription) => {
              if (subscription) sendSubscriptionToBackend(subscription);
            });
          });
        }
        codeRef.current?.blur();
        onAuthenticated();
      })
      .catch((err) => {
        setChecking(false);
        if (err.response) {
          setError(t("rmCodeWrong"));
          setAttn((n) => n + 1);
        } else if (err.request) setError(t("offline_message"));
        else setError(t("suggestionErrorServer"));
      });
  };

  const onCodeChange = (value) => {
    const clean = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6);
    setCode(clean);
    setError("");
    submitCode(clean);
  };

  return (
    <PageShell title={t("rcComplaint")} onBack={() => navigate("/")} currentLang={currentLang} setCurrentLang={setCurrentLang} noInstall formBg>
      <MetaTags />
      <div className={styles.simpleBody}>
        <p className={styles.pageQ}>{t("rmSpace")}</p>
        <Field label={t("rmCodeLabel")} hint={t("rmCodeHint")}>
          <input
            ref={codeRef}
            type="text"
            className={classNames(styles.input, styles.code, { [styles.isOk]: code.length === 6 && !error, [styles.isBad]: error })}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck="false"
            maxLength="6"
            placeholder="••••••"
            value={code}
            readOnly={checking}
            aria-describedby="rmCodeStatus"
            onChange={(e) => onCodeChange(e.target.value)}
          />
        </Field>
        <div id="rmCodeStatus" className={classNames(styles.mcStatus, { [styles.bad]: error })} role="status" aria-live="polite">
          {checking && (
            <>
              <span className={styles.mcSpin} aria-hidden="true" />
              <span>{t("rmVerify")}</span>
            </>
          )}
          {!checking && error}
        </div>
        <div className={styles.or}>{t("rmNoAccount")}</div>
        <button ref={createRef} type="button" className={styles.mcCreate} onClick={() => navigate("/inscription_d")}>
          <StorePlus />
          <span>{t("rmCreate")}</span>
        </button>
        {canInstall && (
          <div className={styles.center}>
            <button type="button" className={styles.mutedLink} onClick={install}>
              <Smartphone size={16} strokeWidth={1.8} aria-hidden />
              <span>{t("rmInstall")}</span>
            </button>
          </div>
        )}
      </div>
      {modals}
    </PageShell>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function ReclamationMerchant({ currentLang, setCurrentLang }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { isStandalone } = usePwaInstall();
  const utmSource = useSelector((state) => state.utm.source); // facebook, instagram, etc
  const merchantFormFields = useSelector((state) => state.tenant.tenant.merchantFormFields) || [];
  const obligatoryMerchantFields = useSelector((state) => state.tenant.tenant.obligatoryMerchantFields) || [];
  // scannerModule gates the camera scanner: tenants without it keep the plain 6-digit entry
  const enabledModules = useSelector((state) => state.tenant?.tenant.enabledModules) || {};
  const has = (field) => merchantFormFields.includes(field);
  const isRequired = (field) => obligatoryMerchantFields.includes(field);

  // synchronize language switch
  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  // null = checking the session cookie | false = code needed | true = merchant known
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  useEffect(() => {
    axios
      .get(`${backendLink}merchant/checkCookie`, { withCredentials: true })
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false));
  }, []);

  // ── Catalog data ────────────────────────────────────────────────────────────

  const [productsList, setProductsList] = useState([]);
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [delays, setDelays] = useState([]);

  useEffect(() => {
    axios
      .get(`${backendLink}shared/getAllProduct`)
      .then((response) => setProductsList(response.data.data.products || []))
      .catch((error) => console.error("Error fetching products:", error));
  }, []);

  // these lists need the merchant session
  useEffect(() => {
    if (!isAuthenticated) return;
    const options = { withCredentials: true };
    axios
      .get(`${backendLink}merchant/getMerchantCompTypes`, options)
      .then((response) => setTypes(response.data.data.complaintTypes || []))
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => setTypesLoading(false));
    axios
      .get(`${backendLink}merchant/fromWhere`, options)
      .then((response) => setSources(response.data.data.values || []))
      .catch((error) => console.error("Error fetching sources:", error));
    axios
      .get(`${backendLink}merchant/fromWhen`, options)
      .then((response) => setDelays(response.data.data.values || []))
      .catch((error) => console.error("Error fetching delays:", error));
  }, [isAuthenticated]);

  // ── Form state ──────────────────────────────────────────────────────────────

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [product, setProduct] = useState(null);
  const [tous, setTous] = useState(false); // complaint about all products
  const [lot, setLot] = useState(EMPTY_LOT);
  const [sourceId, setSourceId] = useState("");
  const [delayId, setDelayId] = useState("");
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]); // [{ file, url }]
  const [voice, setVoice] = useState([]);

  const [stepError, setStepError] = useState("");
  const [error, setError] = useState("");
  const [messageBad, setMessageBad] = useState(0); // shakes the message box when a part of it is missing
  const [busy, setBusy] = useState(false); // compressing a picture
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false); // tracking number once sent

  // Some complaint types are not about a product: the product, lot and delay questions are then skipped
  const noProductFor = (ids) => ids.length > 0 && ids.every((id) => parseInt(types.find((type) => type.id === id)?.has_product) === 0);
  const hasNoProduct = noProductFor(selectedTypes);
  const showDelay = has("from_when_id") && !hasNoProduct;

  const steps = [
    has("type_complaints") && "problem",
    has("products") && !hasNoProduct && "product",
    (has("n_lot") || has("fabrication_date")) && !hasNoProduct && !tous && "lot",
  ].filter(Boolean);
  const drawers = useDrawers(steps);

  useEffect(() => setStepError(""), [drawers.activeStep]);

  const source = sources.find((item) => String(item.id) === sourceId);
  const delayOptions = delays.length > 0 ? delays : DEFAULT_DELAYS;
  const delay = delayOptions.find((item) => String(item.id) === delayId);
  const dateIso = parseDate(lot.dateText);

  // Values in the shape the obligatory-field rules expect
  const formValues = () => ({
    selectedTypes,
    product,
    tous,
    has_no_product: hasNoProduct,
    n_lot: lot.lot,
    date: has("fabrication_date") ? dateIso : "",
    compressedLot: lot.photo ? [lot.photo] : [],
    source,
    delay,
    comment,
    compressedFiles: photos,
    voice_r: voice,
  });

  // ── Steps ───────────────────────────────────────────────────────────────────

  const closeStep = (step, summary) => {
    setError("");
    drawers.close(step, summary);
  };

  const validateStep = (step) => {
    const missing = firstMissing(obligatoryMerchantFields, FIELD_RULES, formValues(), STEP_FIELDS[step]);
    setStepError(missing ? t(missing) : "");
    return !missing;
  };

  const finishProblem = () => {
    if (!validateStep("problem")) return;
    if (hasNoProduct) {
      setProduct(null);
      setTous(false);
      drawers.reset("product", "lot");
    }
    const names = types.filter((type) => selectedTypes.includes(type.id)).map((type) => localized(type, lang));
    closeStep("problem", names.join(" · "));
  };

  // a specific product cancels "all products", and the other way round
  const pickProduct = (picked) => {
    setTous(false);
    setProduct(picked);
    closeStep("product", true);
  };

  const pickAllProducts = () => {
    setProduct(null);
    setTous(true);
    closeStep("product", true);
  };

  const finishLot = () => {
    if (lot.dateText && !dateIso) return setStepError(t("rcDateBad"));
    if (validateStep("lot")) closeStep("lot", lotSummary(lot, t));
  };

  // Summaries of the finished steps, written in the current language each time (a step only
  // remembers that it is done): switching language translates them too
  const summaries = {
    problem: types.filter((type) => selectedTypes.includes(type.id)).map((type) => localized(type, lang)).join(" · "),
    lot: lotSummary(lot, t),
  };
  const summaryOf = (step) => (drawers.done[step] ? summaries[step] || t("rcNotGiven") : t("rcNotGiven"));

  // ── Submit ──────────────────────────────────────────────────────────────────

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending || drawers.firstTodo) return;

    const values = formValues();
    const missing = firstMissing(obligatoryMerchantFields, FIELD_RULES, values);
    if (missing) {
      // reopen the step the missing field belongs to
      const step = steps.find((s) => STEP_FIELDS[s].includes(missing));
      if (step) drawers.open(step);
      if (MESSAGE_FIELDS.includes(missing)) setMessageBad((n) => n + 1);
      flagField(missing, step ? 400 : 0);
      return setError(t(missing));
    }

    const withProduct = !tous && !hasNoProduct;
    const formData = new FormData();
    withProduct && product?.id && formData.append("products[]", product.id);
    selectedTypes.forEach((type) => formData.append("type_complaints[]", type));
    comment && formData.append("comment", xss(comment));
    formData.append("all_products", tous ? 1 : 0);
    formData.append("has_no_product", hasNoProduct ? 1 : 0);
    if (withProduct) {
      lot.lot && formData.append("n_lot", lot.lot);
      values.date && formData.append("fabrication_date", values.date);
    }
    source && formData.append("from_where_id", source.id);
    showDelay && delay && !delay.isDefault && formData.append("from_when_id", delay.id);
    // single audio file (backend expects voice_r, not voice_r[])
    voice[0] && formData.append("voice_r", voice[0]);
    photos.forEach((photo) => formData.append("img_r[]", asFile(photo)));
    withProduct && lot.photo && formData.append("img_l", asFile(lot.photo));
    formData.append("channel_id", isStandalone ? 5 : utmSource || 1);

    setError("");
    setSending(true);
    try {
      await axios.post(`${backendLinkComplaint}merchant/addMerchantComplaint`, formData, {
        withCredentials: true, // Include cookies for merchant identity check
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSent(true);
    } catch (err) {
      console.error("Error sending complaint:", err.response?.data || err);
      setError(t("suggestionErrorServer"));
    } finally {
      setSending(false);
    }
  };

  if (isAuthenticated === false) {
    return <MerchantCode currentLang={currentLang} setCurrentLang={setCurrentLang} onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  const showMedia = has("comment") || has("img_r") || has("voice_r");
  const clearError = () => setError("");
  const drawerProps = (step) => ({ drawers, step, sub: t(`rcStep_${step}`), error: stepError, fields: (STEP_FIELDS[step] || []).join(" ") });
  const toOptions = (items) => items.map((item) => ({ value: String(item.id), label: localized(item, lang) }));

  return (
    <PageShell
      formBg
      title={t("rcComplaint")}
      onBack={() => navigate("/")}
      currentLang={currentLang}
      setCurrentLang={setCurrentLang}
      done={sent && <ThanksScreen title={t("rcOkTitle")} text={t("rcOkText")} onHome={() => navigate("/")} />}
    >
      <MetaTags />
      <div className={styles.cardBody}>
        {isAuthenticated && (
          <form noValidate onSubmit={onSubmit}>
            {has("type_complaints") && (
              <Drawer {...drawerProps("problem")} Icon={CircleAlert} summary={summaryOf("problem")}>
                <Question i18nKey="rcQProblem" />
                <TypeGrid
                  types={types}
                  loading={typesLoading}
                  selected={selectedTypes}
                  onToggle={(id) => {
                    setStepError("");
                    setSelectedTypes((prev) => toggleType(prev, id));
                  }}
                />
                {(selectedTypes.length > 0 || !isRequired("type_complaints")) && <NextButton onClick={finishProblem} />}
              </Drawer>
            )}

            {/* Asked only when the problem is about a product (quality, packaging, labelling…) */}
            {steps.includes("product") && (
              <Drawer
                {...drawerProps("product")}
                Icon={ReceiptText}
                summary={tous ? t("rmAllProducts") : product ? localized(product, lang) : t("rcNotGiven")}
              >
                <Question i18nKey="rmQProduct" center />
                <ProductPicker productsList={productsList} scanner={enabledModules.scannerModule} onPick={pickProduct} onAll={pickAllProducts} />
              </Drawer>
            )}

            {steps.includes("lot") && (
              <Drawer {...drawerProps("lot")} Icon={Tag} summary={summaryOf("lot")}>
                <Question i18nKey="rcQLot" />
                <LotFields
                  lotImage={product?.group?.img}
                  value={lot}
                  onChange={(value) => {
                    setStepError("");
                    setLot(value);
                  }}
                  show={{ lot: has("n_lot"), date: has("fabrication_date") }}
                  isRequired={{ lot: isRequired("n_lot"), date: isRequired("fabrication_date") }}
                  lotPlaceholder={t("lotPlaceholder.default")}
                  lotFilter={LOT_FILTER}
                  onBusy={setBusy}
                />
                <NextButton onClick={finishLot} />
              </Drawer>
            )}

            {/* Last section, as in the prototype: since when, then the message (text, photo, voice), then the supplier */}
            <Reveal open={!drawers.firstTodo}>
              <div className={styles.sectionQ}>
                <Trans i18nKey="rmDetailsQ" components={{ b: <b /> }} />
              </div>
              {showDelay && (
                <div className={styles.mPart}>
                  <div className={styles.fieldLabel}>
                    {t("Depuis quand avez-vous acheté le produit ?")}
                    {isRequired("from_when_id") && <span className={styles.req}> *</span>}
                  </div>
                  <div className={classNames(styles.chips, styles.mcChips)} data-field="from_when_id" role="radiogroup" aria-label={t("Depuis quand avez-vous acheté le produit ?")}>
                    {delayOptions.map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        role="radio"
                        aria-checked={delayId === String(item.id)}
                        className={classNames(styles.chip, { [styles.isSelected]: delayId === String(item.id) })}
                        onClick={() => {
                          clearError();
                          setDelayId(String(item.id));
                        }}
                      >
                        {item.isDefault ? t(item.labelKey) : localized(item, lang)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {showMedia && (
                <div className={styles.mPart}>
                  <MessageLabel
                    required={isRequired("comment") || isRequired("img_r")}
                    count={(comment.trim() ? 1 : 0) + photos.length + voice.length}
                  />
                  <MessageBox
                    show={{ comment: has("comment"), photos: has("img_r"), voice: has("voice_r") }}
                    comment={comment}
                    setComment={(value) => {
                      clearError();
                      setComment(value);
                    }}
                    photos={photos}
                    setPhotos={setPhotos}
                    setVoice={setVoice}
                    onBusy={setBusy}
                    bad={messageBad}
                  />
                </div>
              )}
              {has("from_where_id") && (
                <div className={styles.mPart}>
                  <Field label={t("rmQFromWhere")} required={isRequired("from_where_id")} field="from_where_id">
                    <Select
                      value={sourceId}
                      placeholder={t("rmSupplierPh")}
                      options={toOptions(sources)}
                      onChange={(id) => {
                        clearError();
                        setSourceId(id);
                      }}
                    />
                  </Field>
                </div>
              )}
              <ErrorBox>{error}</ErrorBox>
              <button type="submit" className={classNames(styles.submit, { [styles.isSuccess]: sent })} disabled={sending || sent || busy}>
                <Send size={17} strokeWidth={2.2} aria-hidden />
                <span>{sent ? t("rcSent") : t("rcSend")}</span>
              </button>
            </Reveal>
          </form>
        )}
      </div>
    </PageShell>
  );
}
